// @ts-nocheck
import {
  SPELLS,
  isLocalDev,
} from "./data";
import {
  TAU,
  clamp,
  rgba,
  shade,
} from "./util";
import {
  Sound,
} from "./audio";

/* ---------------- Warlock sprite generator ----------------
   Pre-renders a chibi wizard (oblique top-down) into an offscreen canvas,
   facing "up" (toward -Y): pointy starry hat, bearded face, robe, staff+orb.
   Drawn once per color, then rotated to the aim direction each frame.
   SPRITE_BRIM is the body half-width reference used to scale to playerRadius. */
export const SPRITE_SIZE = 150;
export const SPRITE_BRIM = 24;

export function drawStar(c, x, y, r, color) {
  c.save();
  c.fillStyle = color;
  c.beginPath();
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + i * TAU / 5;
    c.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
    const a2 = a + TAU / 10;
    c.lineTo(x + Math.cos(a2) * r * 0.45, y + Math.sin(a2) * r * 0.45);
  }
  c.closePath();
  c.fill();
  c.restore();
}

// A hooded warlock in ³⁄₄ profile, standing upright and facing +X (right):
// cloak trailing behind, robe, a pointed cowl whose peak and opening lean
// forward, glowing eyes set toward the front, and an outstretched arm cupping
// a channeled orb out front. Pre-rendered per color; the draw step mirrors it
// horizontally to turn the warlock left/right toward its heading.
/* ---- 8-direction warlock art (loaded from /sprites) ----
   Five themed characters; each player is assigned one (roughly matching its
   colour). Eight rotation PNGs per character are picked by facing direction. */
export const WARLOCK_CHARS = ["frost", "ember", "verdant", "celestial", "void", "void", "frost", "ember"];
export const WARLOCK_DIRS = ["east", "south-east", "south", "south-west", "west", "north-west", "north", "north-east"];
/** Themes with cast/<dir>-{mid,end}.png art. Add a theme id here once its cast art lands
   (mirrors BOSS_CAST_IDS — listing a theme with no art on disk fetches 404s every round).
   Eventual target: ["frost", "ember", "verdant", "celestial", "void"]. */
export const WARLOCK_CAST_CHARS = ["frost", "ember", "verdant", "celestial", "void"];
const WARLOCK_CAST_CHAR_SET = new Set(WARLOCK_CAST_CHARS);
export const WARLOCK_IMG = {};      // char -> { dir: Image }
export const WARLOCK_CAST_IMG = {}; // char -> { dir: { mid: Image, end: Image } }

/* ---- Warlock sprites load on demand, only for themes in the current room ----
   Idle billboards self-trigger via warlockImageByOct (any draw context, e.g. lobby
   previews). ensureWarlockAssets(themes) warms idle + cast poses at round start for the
   themes actually present; draw falls back to the procedural wizard until decode completes. */
function loadWarlockIdle(ch) {
  if (WARLOCK_IMG[ch]) return WARLOCK_IMG[ch];
  const set = {};
  for (const d of WARLOCK_DIRS) {
    const im = new Image();
    im.src = `sprites/${ch}/${d}.png`;
    set[d] = im;
  }
  WARLOCK_IMG[ch] = set;
  return set;
}

function loadWarlockCast(ch) {
  if (WARLOCK_CAST_IMG[ch]) return WARLOCK_CAST_IMG[ch];
  if (!WARLOCK_CAST_CHAR_SET.has(ch)) return null;
  const set = {};
  for (const d of WARLOCK_DIRS) {
    const mid = new Image();
    mid.src = `sprites/${ch}/cast/${d}-mid.png`;
    const end = new Image();
    end.src = `sprites/${ch}/cast/${d}-end.png`;
    set[d] = { mid, end };
  }
  WARLOCK_CAST_IMG[ch] = set;
  return set;
}

/** Preload idle + cast billboards for the warlock themes present in a round. */
export function ensureWarlockAssets(themes) {
  if (!themes) return;
  for (const ch of themes) {
    loadWarlockIdle(ch);
    loadWarlockCast(ch);
  }
}

/* ---- 8-direction boss art (sandbox preview targets) ----
   Canvas combat bodies (no 8-dir PNGs): aurelion, ixtholoth — see drawAurelionRotating / drawIxtholothEye.
   Intro portraits: ixtholoth/portrait, sisters/portrait (combined briefing); morwenna/ + lys/ are in-fight sprites. */
// Aurelion: canvas-rotated top-down body (see drawAurelionRotating) — not 8-dir billboards.
export const PVE_BOSS_NAMES = { angel: "Inferna", titan: "Titan", aurelion: "Aurelion", groshkar: "Grosh'kar", kael: "Kael", vorathal: "Vorathal", ixtholoth: "Ix'tholoth", sisters: "The Twin Sisters", morwenna: "Morwenna", lys: "Lys" };
/** Full boss display names (matches server BossDef.DisplayName). */
export const PVE_BOSS_DISPLAY_NAMES = { angel: "Inferna", titan: "Titan", aurelion: "Aurelion", groshkar: "Grosh'kar", kael: "Kael the Unbound", vorathal: "Vorathal", ixtholoth: "Ix'tholoth", sisters: "The Twin Sisters", morwenna: "Sister Morwenna", lys: "Sister Lys" };
/** Fixed PvE gauntlet order (mirrors server/sim PvEGauntlet). */
export const PVE_GAUNTLET = ["angel", "titan", "aurelion", "groshkar", "vorathal", "ixtholoth", "kael", "sisters"];
export function pveBossName(id) { return PVE_BOSS_NAMES[id] || id || "Boss"; }
export function pveBossDisplayName(id) { return PVE_BOSS_DISPLAY_NAMES[id] || pveBossName(id); }

/** Display label for PvE boss minions (matches server NewPvEAdd names). */
export function pveAddDisplayName(kind) {
  if (kind === "cinder") return "Cinder";
  if (kind === "spore") return "Spore";
  if (kind === "rime") return "Rime";
  return "Minion";
}

export const BOSS_IDS = ["angel", "titan", "aurelion", "groshkar", "kael", "vorathal", "ixtholoth", "morwenna", "lys"];
/** Bosses with cast/<dir>-{mid,end}.png art; omit ids here to avoid 404s on optional poses. */
export const BOSS_CAST_IDS = ["kael", "angel", "titan", "groshkar", "vorathal", "morwenna", "lys"];
const BOSS_CAST_ID_SET = new Set(BOSS_CAST_IDS);
/** Summoned add billboards per boss, loaded alongside the boss that spawns them. */
const BOSS_ADD_KINDS = { angel: ["cinder"], groshkar: ["spore"], vorathal: ["rime"] };
/** Round boss id → billboard sprite folder(s). The sisters encounter uses morwenna + lys. */
const BOSS_SPRITE_IDS = { sisters: ["morwenna", "lys"] };
const ADD_KIND_SRC = {
  cinder: "sprites/bosses/angel/cinder",
  spore: "sprites/bosses/groshkar/spore",
  rime: "sprites/bosses/vorathal/rime",
};

/* ---- Boss sprites load on demand (idle billboards, cast poses, summoned adds) ----
   Boss art is large (~20 MB of cast frames across all bosses) and only drawn in PvE/sandbox,
   so nothing loads at startup. PvP never fetches boss PNGs.
   ensureBossAssets(id) preloads idle + adds when a PvE round starts (before the boss appears).
   Cast frames (~16 PNGs per boss) load only on first cast via touchBossCast — draw falls back
   to idle until decode completes. Accessors self-trigger idle/add loaders for sandbox spawns. */
export const BOSS_IMG = {}; // id -> { dir: Image }
export const BOSS_CAST_IMG = {}; // id -> { dir: { mid: Image, end: Image } }
export const ADD_KIND_IMG = {}; // kind -> { dir: Image }

function bossSpriteIds(bossId) {
  return BOSS_SPRITE_IDS[bossId] || (bossId ? [bossId] : []);
}

function loadBossIdle(id) {
  if (BOSS_IMG[id]) return BOSS_IMG[id];
  if (id === "aurelion" || id === "ixtholoth") return null; // canvas body only
  const set = {};
  for (const d of WARLOCK_DIRS) {
    const im = new Image();
    im.src = `sprites/bosses/${id}/${d}.png`;
    set[d] = im;
  }
  BOSS_IMG[id] = set;
  return set;
}

function loadBossCast(id) {
  if (BOSS_CAST_IMG[id]) return BOSS_CAST_IMG[id];
  if (!BOSS_CAST_ID_SET.has(id)) return null;
  const set = {};
  for (const d of WARLOCK_DIRS) {
    const mid = new Image();
    mid.src = `sprites/bosses/${id}/cast/${d}-mid.png`;
    const end = new Image();
    end.src = `sprites/bosses/${id}/cast/${d}-end.png`;
    set[d] = { mid, end };
  }
  BOSS_CAST_IMG[id] = set;
  return set;
}

function loadAddKind(kind) {
  if (ADD_KIND_IMG[kind]) return ADD_KIND_IMG[kind];
  const base = ADD_KIND_SRC[kind];
  if (!base) return null;
  const set = {};
  for (const d of WARLOCK_DIRS) {
    const im = new Image();
    im.src = `${base}/${d}.png`;
    set[d] = im;
  }
  ADD_KIND_IMG[kind] = set;
  return set;
}

/** Preload idle billboards + summoned adds for a PvE round (not cast poses). */
export function ensureBossAssets(bossId) {
  if (!bossId) return;
  for (const id of bossSpriteIds(bossId)) {
    loadBossIdle(id);
    for (const kind of BOSS_ADD_KINDS[id] || []) loadAddKind(kind);
  }
}

/** Start fetching cast poses when a boss first casts (~16 PNGs); no-op if already loading. */
export function touchBossCast(bossId) {
  if (!bossId || !BOSS_CAST_ID_SET.has(bossId)) return;
  loadBossCast(bossId);
}

/* ---- Spell spritesheets (strips or cols×rows grids, prog-synced to armDelay) ----
   Register spell ids here; assets live under sprites/spells/<id>/.
   Arm-timed entities: armTimedEntityFromWire + armTimedProg + drawSpellSprite.

   Phased spells (cast / flight / impact): put sheets under `phases`, tune on the
   same object with <phase><Setting> keys — all optional, read by spellSpritePhaseOpt.
     drawScale     — multiplies every phase size
     castScale     — draw size ≈ (castRadius || worldR) * castScale * drawScale
     castLife      — seconds for the cast pop (4 frames spread over this)
     castRadius    — world-units base when spawn omits worldR
     castFadeStart — alpha fade begins at this life fraction (0–1)
     castOffset    — shift cast pop along aim (world units) in front of caster center
     castLayer     — "front" (over wizard) or "behind" (under wizard)
     flightScale   — draw size ≈ projectile.radius * flightScale * drawScale
     flightFps     — loop speed while in flight
     impactScale   — draw size ≈ spell.radius * impactScale * drawScale
     impactLife    — seconds for the impact pop
     impactFadeStart
   Add more <phase><Setting> keys anytime; wire them in spellSpritePhaseOpt below. */
export const SPELL_SPRITE_SHEETS = {
  bloompod: {
    src: "sprites/spells/bloompod/bloom.png",
    frames: 16,
    cols: 4,           // 4×4 grid on 1024×1024 → 256×256 cells (derived on load)
    chargeFrames: 12,  // wind-up uses first 12 frames only; row 4 reserved for burst art
    drawScale: 1.12,
    fixedSize: true,   // growth is in the art; don't also scale draw size with prog
  },
  // fireball: {
  //   phases: {
  //     cast:   { src: "sprites/spells/fireball/cast_fireball.png",   frames: 4, cols: 4 },
  //     flight: { src: "sprites/spells/fireball/flight_fireball.png", frames: 4, cols: 4 },
  //     impact: { src: "sprites/spells/fireball/impact_fireball.png", frames: 4, cols: 4 },
  //   },
  //   castScale: 2.4,
  //   castLife: 1,
  //   castRadius: 70,
  //   castFadeStart: 0.72,
  //   castOffset: 38,
  //   castLayer: "front",
  //   flightScale: 10,
  //   flightFps: 14,
  //   impactScale: 3.1,
  //   impactLife: 0.4,
  //   impactFadeStart: 0.72,
  // },
};
export const SPELL_SPRITE_IMG = {}; // spellId -> Image (single-sheet spells only)
export const FIREBALL_SPRITE_SPELLS = new Set(["fireball", "firespray"]);

export function fireballSpriteFXEnabled(spellId) {
  return FIREBALL_SPRITE_SPELLS.has(spellId) && !!spellSpriteSpec("fireball")?.phases;
}

/* ---- Spell sprite dev tuning (local sandbox only) ---- */
export const SPELL_SPRITE_DEV_LS = "wlkSpellSpriteDev";
export const SPELL_SPRITE_DEV_SCHEMA = {
  fireball: [
    { key: "drawScale", label: "Global scale", group: "All", type: "range", min: 0.2, max: 20, step: 0.05 },
    { key: "castScale", label: "Cast scale", group: "Cast", type: "range", min: 0.2, max: 20, step: 0.1 },
    { key: "castLife", label: "Cast duration (s)", group: "Cast", type: "range", min: 0.05, max: 3, step: 0.01 },
    { key: "castRadius", label: "Cast base radius", group: "Cast", type: "range", min: 8, max: 120, step: 1 },
    { key: "castOffset", label: "Cast offset (forward)", group: "Cast", type: "range", min: 0, max: 120, step: 1 },
    { key: "castFadeStart", label: "Cast fade start", group: "Cast", type: "range", min: 0, max: 1, step: 0.01 },
    { key: "castLayer", label: "Cast layer (z)", group: "Cast", type: "select", options: ["front", "behind"] },
    { key: "flightScale", label: "Flight scale", group: "Flight", type: "range", min: 0.2, max: 20, step: 0.1 },
    { key: "flightFps", label: "Flight FPS", group: "Flight", type: "range", min: 1, max: 60, step: 1 },
    { key: "impactScale", label: "Impact scale", group: "Impact", type: "range", min: 0.2, max: 20, step: 0.1 },
    { key: "impactLife", label: "Impact duration (s)", group: "Impact", type: "range", min: 0.05, max: 3, step: 0.01 },
    { key: "impactFadeStart", label: "Impact fade start", group: "Impact", type: "range", min: 0, max: 1, step: 0.01 },
  ],
};
export const SPELL_SPRITE_DEV_DEFAULTS = {};
(function snapshotSpellSpriteDevDefaults() {
  for (const [id, fields] of Object.entries(SPELL_SPRITE_DEV_SCHEMA)) {
    const spec = SPELL_SPRITE_SHEETS[id];
    if (!spec) continue;
    const snap = {};
    for (const f of fields) snap[f.key] = spec[f.key];
    SPELL_SPRITE_DEV_DEFAULTS[id] = snap;
  }
})();

export function spellSpriteDevEnabled() {
  return isLocalDev();
}

export function spellSpriteDevTunableKeys(spellId) {
  return (SPELL_SPRITE_DEV_SCHEMA[spellId] || []).map(f => f.key);
}

export function spellSpriteDevLoadAll() {
  if (!spellSpriteDevEnabled()) return;
  try {
    const raw = JSON.parse(localStorage.getItem(SPELL_SPRITE_DEV_LS) || "{}");
    for (const id of Object.keys(raw)) {
      const spec = SPELL_SPRITE_SHEETS[id];
      if (!spec || !raw[id]) continue;
      for (const k of spellSpriteDevTunableKeys(id)) {
        if (raw[id][k] != null) spec[k] = raw[id][k];
      }
    }
  } catch (e) {}
}

export function spellSpriteDevSaveSpell(spellId) {
  if (!spellSpriteDevEnabled()) return;
  const spec = SPELL_SPRITE_SHEETS[spellId];
  if (!spec) return;
  try {
    const all = JSON.parse(localStorage.getItem(SPELL_SPRITE_DEV_LS) || "{}");
    const slice = {};
    for (const k of spellSpriteDevTunableKeys(spellId)) slice[k] = spec[k];
    all[spellId] = slice;
    localStorage.setItem(SPELL_SPRITE_DEV_LS, JSON.stringify(all));
  } catch (e) {}
}

export function spellSpriteDevVisible(game) {
  return spellSpriteDevEnabled() && game
    && game.matchMode === "sandbox" && game.state === "playing";
}

export let _spellSpriteDevGame = null;
export let _spellSpriteDevSpellId = "fireball";

export function updateSpellSpriteDevPanelVisibility(game) {
  _spellSpriteDevGame = game;
  const root = document.getElementById("spellSpriteDev");
  if (!root) return;
  root.classList.toggle("hidden", !spellSpriteDevVisible(game));
}

export function spellSpriteDevSetField(spellId, key, value) {
  const spec = SPELL_SPRITE_SHEETS[spellId];
  if (!spec) return;
  spec[key] = value;
  spellSpriteDevSaveSpell(spellId);
}

export function refreshSpellSpriteDevFields() {
  const host = document.getElementById("spellSpriteDevFields");
  const sel = document.getElementById("spellSpriteDevSpell");
  if (!host || !sel) return;
  const spellId = sel.value || _spellSpriteDevSpellId;
  _spellSpriteDevSpellId = spellId;
  const spec = SPELL_SPRITE_SHEETS[spellId];
  const fields = SPELL_SPRITE_DEV_SCHEMA[spellId];
  if (!spec || !fields) {
    host.innerHTML = "<p class=\"spell-sprite-dev-empty\">No dev schema for this spell.</p>";
    return;
  }
  host.innerHTML = "";
  let group = "";
  for (const f of fields) {
    if (f.group !== group) {
      group = f.group;
      const gh = document.createElement("div");
      gh.className = "spell-sprite-dev-group";
      gh.textContent = group;
      host.appendChild(gh);
    }
    const row = document.createElement("div");
    row.className = "spell-sprite-dev-row";
    const lab = document.createElement("label");
    lab.textContent = f.label;
    row.appendChild(lab);
    const apply = (val) => {
      const v = f.type === "select" ? val : +val;
      spellSpriteDevSetField(spellId, f.key, v);
      spellSpriteDevSyncRowDisplay(row, f, spec[f.key]);
    };
    if (f.type === "select") {
      const dd = document.createElement("select");
      dd.className = "ssd-select";
      for (const opt of f.options) {
        const o = document.createElement("option");
        o.value = opt;
        o.textContent = opt;
        dd.appendChild(o);
      }
      dd.value = spec[f.key] != null ? String(spec[f.key]) : f.options[0];
      dd.onchange = () => apply(dd.value);
      row.appendChild(dd);
    } else {
      const rng = document.createElement("input");
      rng.type = "range";
      rng.min = f.min;
      rng.max = f.max;
      rng.step = f.step;
      rng.value = spec[f.key];
      const val = document.createElement("span");
      val.className = "ssd-val";
      rng.oninput = () => apply(rng.value);
      row.appendChild(rng);
      row.appendChild(val);
    }
    host.appendChild(row);
    spellSpriteDevSyncRowDisplay(row, f, spec[f.key]);
  }
}

export function spellSpriteDevSyncRowDisplay(row, field, value) {
  if (field.type === "select") return;
  const val = row.querySelector(".ssd-val");
  if (!val) return;
  const n = +value;
  val.textContent = Number.isInteger(field.step) && field.step >= 1
    ? String(Math.round(n)) : n.toFixed(2).replace(/\.?0+$/, "");
  const rng = row.querySelector('input[type="range"]');
  if (rng && String(rng.value) !== String(value)) rng.value = value;
}

export function spellSpriteDevResetSpell(spellId) {
  const defs = SPELL_SPRITE_DEV_DEFAULTS[spellId];
  const spec = SPELL_SPRITE_SHEETS[spellId];
  if (!defs || !spec) return;
  Object.assign(spec, defs);
  spellSpriteDevSaveSpell(spellId);
  refreshSpellSpriteDevFields();
}

export function spellSpriteDevCopySpell(spellId) {
  const spec = SPELL_SPRITE_SHEETS[spellId];
  if (!spec) return;
  const out = {};
  for (const k of spellSpriteDevTunableKeys(spellId)) out[k] = spec[k];
  const text = JSON.stringify(out, null, 2);
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
  }
  console.log("[spell sprite dev]", spellId, out);
}

export function spellSpriteDevTestPop(phase) {
  const g = _spellSpriteDevGame;
  if (!g || !spellSpriteDevVisible(g)) return;
  const spellId = document.getElementById("spellSpriteDevSpell")?.value || _spellSpriteDevSpellId;
  const h = g.human();
  if (!h) return;
  const aim = h.aim ?? h.facing ?? 0;
  const pose = spellCastPopPose(spellId, h.x, h.y, aim);
  const sp = SPELLS[spellId] || SPELLS.fireball;
  const worldR = (sp && sp.radius) || 13;
  g.spawnSpellSpritePop(pose.x, pose.y, spellId, phase, {
    ang: pose.ang,
    worldR: phase === "impact" ? worldR : undefined,
  });
}

export function wireSpellSpriteDevPanel() {
  if (!spellSpriteDevEnabled()) return;
  const root = document.getElementById("spellSpriteDev");
  const sel = document.getElementById("spellSpriteDevSpell");
  if (!root || !sel) return;
  sel.innerHTML = "";
  for (const id of Object.keys(SPELL_SPRITE_DEV_SCHEMA)) {
    if (!SPELL_SPRITE_SHEETS[id]) continue;
    const o = document.createElement("option");
    o.value = id;
    o.textContent = SPELLS[id]?.name || id;
    sel.appendChild(o);
  }
  sel.value = _spellSpriteDevSpellId;
  sel.onchange = () => refreshSpellSpriteDevFields();
  document.getElementById("spellSpriteDevReset")?.addEventListener("click", () => {
    Sound.ui();
    spellSpriteDevResetSpell(sel.value);
  });
  document.getElementById("spellSpriteDevCopy")?.addEventListener("click", () => {
    Sound.ui();
    spellSpriteDevCopySpell(sel.value);
  });
  document.getElementById("spellSpriteDevTestCast")?.addEventListener("click", () => {
    Sound.ui();
    spellSpriteDevTestPop("cast");
  });
  document.getElementById("spellSpriteDevTestImpact")?.addEventListener("click", () => {
    Sound.ui();
    spellSpriteDevTestPop("impact");
  });
  refreshSpellSpriteDevFields();
}

spellSpriteDevLoadAll();

export function bakeSpellSpriteFrames(spellId, spec, im) {
  if (!spec.frames || !im.naturalWidth) return;
  const cols = spec.cols || spec.frames;
  const rows = Math.ceil(spec.frames / cols);
  const cellW = Math.floor(im.naturalWidth / cols);
  const cellH = Math.floor(im.naturalHeight / rows);
  spec.frameW = cellW;
  spec.frameH = cellH;
  if (!spec.cols || spec.frames <= 1) return;
  const baked = [];
  for (let fi = 0; fi < spec.frames; fi++) {
    const col = fi % cols;
    const row = Math.floor(fi / cols);
    const cv = document.createElement("canvas");
    cv.width = cellW;
    cv.height = cellH;
    const cx = cv.getContext("2d");
    cx.imageSmoothingEnabled = false;
    cx.drawImage(im, col * cellW, row * cellH, cellW, cellH, 0, 0, cellW, cellH);
    baked.push(cv);
  }
  spec.bakedFrames = baked;
}

(function loadSpellSpriteSheets() {
  for (const [id, spec] of Object.entries(SPELL_SPRITE_SHEETS)) {
    if (spec.phases) {
      for (const pspec of Object.values(spec.phases)) {
        const im = new Image();
        im.onload = () => bakeSpellSpriteFrames(id, pspec, im);
        im.src = pspec.src;
        pspec._img = im;
      }
      continue;
    }
    const im = new Image();
    im.onload = () => bakeSpellSpriteFrames(id, spec, im);
    im.src = spec.src;
    SPELL_SPRITE_IMG[id] = im;
  }
})();

export function spellSpriteSpec(spellId) {
  return SPELL_SPRITE_SHEETS[spellId] || null;
}

export function spellSpriteArtId(spellId) {
  if (spellSpriteSpec(spellId)?.phases) return spellId;
  if (fireballSpriteFXEnabled(spellId)) return "fireball";
  return spellId;
}

export function spellSpritePhaseSpec(spellId, phase) {
  const artId = spellSpriteArtId(spellId);
  const spec = spellSpriteSpec(artId);
  if (!spec) return null;
  if (spec.phases) return phase ? (spec.phases[phase] || null) : null;
  return spec;
}

export function spellSpritePhaseImage(spellId, phase) {
  const pspec = spellSpritePhaseSpec(spellId, phase);
  if (pspec?._img) return pspec._img;
  const artId = spellSpriteArtId(spellId);
  return SPELL_SPRITE_IMG[artId] || null;
}

export function spellSpritePhaseReady(spellId, phase) {
  const pspec = spellSpritePhaseSpec(spellId, phase);
  const im = spellSpritePhaseImage(spellId, phase);
  if (!pspec || !im || !im.complete || !im.naturalWidth) return false;
  if (pspec.cols && pspec.frames > 1) {
    return pspec.bakedFrames && pspec.bakedFrames.length === pspec.frames;
  }
  return true;
}

export function spellSpriteSheetReady(spellId) {
  const artId = spellSpriteArtId(spellId);
  const spec = spellSpriteSpec(artId);
  if (!spec) return false;
  if (spec.phases) {
    return Object.keys(spec.phases).every(ph => spellSpritePhaseReady(spellId, ph));
  }
  return spellSpritePhaseReady(spellId, null);
}

export function spellSpriteArtSpec(spellId) {
  return spellSpriteSpec(spellSpriteArtId(spellId));
}

// Per-phase tuning from SPELL_SPRITE_SHEETS[<id>].<phase><Setting> (e.g. castLife).
export function spellSpritePhaseOpt(spellId, phase, setting, fallback) {
  const spec = spellSpriteArtSpec(spellId);
  if (!spec || !phase) return fallback;
  const v = spec[phase + setting];
  return v != null ? v : fallback;
}

export function spellSpritePhaseDrawSz(spellId, phase, worldR) {
  const spec = spellSpriteArtSpec(spellId);
  const sc = (spec && spec.drawScale) || 1;
  const phaseScale = spellSpritePhaseOpt(spellId, phase, "Scale", null);
  if (phaseScale != null) {
    const base = worldR != null ? worldR
      : spellSpritePhaseOpt(spellId, phase, "Radius", 13);
    return base * phaseScale * sc;
  }
  const r = worldR || 13;
  return r * 2 * sc;
}

export function spellSpritePhaseLife(spellId, phase) {
  return spellSpritePhaseOpt(spellId, phase, "Life", 0.35);
}

export function spellSpritePhaseFps(spellId, phase) {
  return spellSpritePhaseOpt(spellId, phase, "Fps", 12);
}

export function spellCastPopPose(spellId, x, y, aim) {
  const ang = aim != null && Number.isFinite(aim) ? aim : 0;
  const off = spellSpritePhaseOpt(spellId, "cast", "Offset", 0);
  return {
    x: x + Math.cos(ang) * off,
    y: y + Math.sin(ang) * off,
    ang,
  };
}

export function spellCastPopLayer(spellId) {
  const layer = spellSpritePhaseOpt(spellId, "cast", "Layer", "front");
  return layer === "behind" ? "behind" : "front";
}

export function spellSpriteAnimFrame(spellId, phase, t) {
  const pspec = spellSpritePhaseSpec(spellId, phase);
  const n = (pspec && pspec.frames) || 1;
  const fps = spellSpritePhaseFps(spellId, phase);
  return Math.floor(t * fps) % n;
}

export function spellArmDelay(spellId) {
  const sp = SPELLS[spellId];
  return (sp && sp.armDelay) || 0;
}

export function spellSpriteFrameCount(spellId, forCharge) {
  const spec = spellSpriteSpec(spellId);
  if (!spec) return 1;
  if (forCharge !== false && spec.chargeFrames) return spec.chargeFrames;
  return spec.frames;
}

export function spellSpriteFrameIndex(spellId, prog, forCharge) {
  const spec = spellSpriteSpec(spellId);
  if (!spec) return 0;
  const n = spellSpriteFrameCount(spellId, forCharge);
  if (n <= 1) return 0;
  const p = clamp(prog, 0, 1);
  return clamp(Math.min(n - 1, Math.floor(p * n)), 0, n - 1);
}

export function armTimedProg(entity) {
  return clamp(entity.progBase || 0, 0, 1);
}

export function spellSpriteFrameRect(spec, im, fi) {
  const cols = spec.cols || spec.frames;
  const rows = Math.ceil(spec.frames / cols);
  const cellW = spec.frameW || Math.floor(im.naturalWidth / cols);
  const cellH = spec.frameH || Math.floor(im.naturalHeight / rows);
  const col = fi % cols;
  const row = Math.floor(fi / cols);
  const inset = spec.sourceInset || 0;
  const sx = col * cellW + inset;
  const sy = row * cellH + inset;
  const fw = cellW - inset * 2;
  const fh = cellH - inset * 2;
  return { sx, sy, fw, fh };
}

export function spellSpriteDrawSz(spellId, blastR, prog) {
  const spec = spellSpriteSpec(spellId);
  const p = (spec && spec.fixedSize) ? 1 : clamp(prog, 0, 1);
  const growR = blastR * (0.25 + 0.75 * p);
  return growR * ((spec && spec.drawScale) || 1);
}

export function healPodFromWire(h, now, old) {
  const pg = h.pg || 0;
  const spellId = "bloompod";
  const same = old && Math.hypot(old.x - h.x, old.y - h.y) < 6;
  return {
    x: h.x, y: h.y, r: h.r, spellId,
    progBase: pg, team: h.tm,
    pulse: same ? old.pulse : now,
  };
}
export function drawArmRangeTelegraph(c, maxR, prog, col, opts = {}) {
  const p = clamp(prog, 0, 1);
  const pulse = opts.pulse != null ? opts.pulse : 1;
  const fillCol = opts.fillCol || col;
  const fillR = maxR * p;
  c.globalCompositeOperation = "source-over";
  if (fillR > 0.5) {
    c.fillStyle = rgba(fillCol, (opts.fillA0 || 0.06) + (opts.fillA1 || 0.1) * p);
    c.beginPath(); c.arc(0, 0, fillR, 0, TAU); c.fill();
  }
  c.strokeStyle = rgba(col, (opts.ringA0 || 0.35) + (opts.ringA1 || 0.2) * pulse);
  c.lineWidth = opts.lineW || 2.5;
  if (opts.dash) {
    c.setLineDash(opts.dash);
    if (opts.dashOffset != null) c.lineDashOffset = opts.dashOffset;
  }
  c.beginPath(); c.arc(0, 0, maxR, 0, TAU); c.stroke();
  if (opts.dash) c.setLineDash([]);
}

// Wind-up accent: progress arc + inward sparks (Scourge / Sanctuary channel).
export function drawArmChargeAccent(c, bodyR, prog, col, fillCol, t) {
  const p = clamp(prog, 0, 1);
  c.globalAlpha = 0.95; c.strokeStyle = col; c.lineWidth = 4;
  c.beginPath(); c.arc(0, 0, bodyR + 10, -Math.PI / 2, -Math.PI / 2 + TAU * p); c.stroke();
  c.globalAlpha = 0.8; c.fillStyle = fillCol;
  for (let i = 0; i < 4; i++) {
    const a = t * 7 + i * (TAU / 4);
    const rr = bodyR + 18 * (1 - (t * 3 + i) % 1);
    c.beginPath(); c.arc(Math.cos(a) * rr, Math.sin(a) * rr, 2, 0, TAU); c.fill();
  }
}

// Draw one frame centered at the current transform. Returns false if not ready.
export function drawSpellSprite(c, spellId, prog, drawSz, alpha, frameIndex, phase) {
  const artId = spellSpriteArtId(spellId);
  const pspec = spellSpritePhaseSpec(spellId, phase);
  const sheetSpec = pspec || spellSpriteSpec(artId);
  if (!sheetSpec || !spellSpritePhaseReady(spellId, phase)) return false;
  const im = spellSpritePhaseImage(spellId, phase);
  const fi = frameIndex != null ? frameIndex
    : (phase ? spellSpriteAnimFrame(spellId, phase, prog)
      : spellSpriteFrameIndex(artId, prog, true));
  const baked = sheetSpec.bakedFrames && sheetSpec.bakedFrames[fi];
  const fw = sheetSpec.frameW || im.naturalWidth;
  const fh = sheetSpec.frameH || im.naturalHeight;
  const sz = Math.round(drawSz != null ? drawSz : fw);
  const half = sz / 2;
  const smooth = c.imageSmoothingEnabled;
  c.imageSmoothingEnabled = false;
  const blit = () => {
    if (baked) c.drawImage(baked, 0, 0, fw, fh, -half, -half, sz, sz);
    else {
      const { sx, sy, fw: sw, fh: sh } = spellSpriteFrameRect(sheetSpec, im, fi);
      c.drawImage(im, sx, sy, sw, sh, -half, -half, sz, sz);
    }
  };
  if (alpha != null && alpha < 1) {
    c.save();
    c.globalAlpha *= clamp(alpha, 0, 1);
    blit();
    c.restore();
  } else blit();
  c.imageSmoothingEnabled = smooth;
  return true;
}

// dirForFacing maps a heading angle (atan2(dy,dx), +x east, +y south) to one of
// the 8 compass rotations.
export function octIndex(a) {
  let oct = Math.round(a / (Math.PI / 4)); // -4..4
  return ((oct % 8) + 8) % 8;             // 0..7  (0=east, 2=south, 4=west, 6=north)
}
export function dirForFacing(a) { return WARLOCK_DIRS[octIndex(a)]; }

// Returns the loaded directional image for a player, or null if not ready yet.
export function warlockImage(index, facing) {
  return warlockImageByOct(index, octIndex(facing));
}
export function warlockImageByOct(index, oct) {
  const ch = WARLOCK_CHARS[index % WARLOCK_CHARS.length];
  const set = loadWarlockIdle(ch);
  if (!set) return null;
  const img = set[WARLOCK_DIRS[oct]];
  return (img && img.complete && img.naturalWidth > 0) ? img : null;
}

export function warlockCastImageByOct(index, oct, phase) {
  const ch = WARLOCK_CHARS[index % WARLOCK_CHARS.length];
  const set = loadWarlockCast(ch);
  if (!set) return null;
  const pair = set[WARLOCK_DIRS[oct]];
  if (!pair) return null;
  const img = phase === "end" ? pair.end : pair.mid;
  return (img && img.complete && img.naturalWidth > 0) ? img : null;
}

export function bossImage(bossId, facing) {
  return bossImageByOct(bossId, octIndex(facing));
}
export function bossImageByOct(bossId, oct) {
  const set = loadBossIdle(bossId);
  if (!set) return null;
  const img = set[WARLOCK_DIRS[oct]];
  return (img && img.complete && img.naturalWidth > 0) ? img : null;
}

export function bossCastImageByOct(bossId, oct, phase) {
  const set = loadBossCast(bossId);
  if (!set) return null;
  const pair = set[WARLOCK_DIRS[oct]];
  if (!pair) return null;
  const img = phase === "end" ? pair.end : pair.mid;
  return (img && img.complete && img.naturalWidth > 0) ? img : null;
}

export function addKindImageByOct(kind, oct) {
  const set = loadAddKind(kind);
  if (!set) return null;
  const img = set[WARLOCK_DIRS[oct]];
  return (img && img.complete && img.naturalWidth > 0) ? img : null;
}

export function imgW(img) { return img?.naturalWidth || img?.width || 0; }
export function imgH(img) { return img?.naturalHeight || img?.height || 0; }

// Ground shadow anchored to sprite feet. Boss art is much taller than wizards but
// used the same small shadow at the entity origin, so bosses looked like they float.
export function drawEntityGroundShadow(c, p) {
  if (p.falling) return;
  const face = Number.isFinite(p.facing) ? p.facing : -Math.PI / 2;
  if (p.boss) {
    if (p.boss === "aurelion" || p.boss === "ixtholoth") {
      const coreR = p.radius * (p.boss === "ixtholoth" ? 3.8 : 3.4);
      c.fillStyle = "rgba(0,0,0,0.38)";
      c.beginPath(); c.ellipse(0, p.radius * 0.15, coreR * 1.05, coreR * 0.42, 0, 0, TAU); c.fill();
      return;
    }
    const baseH = p.boss === "kael" ? p.radius * 9.5 : p.radius * 10.5;
    const img = bossImage(p.boss, face);
    const aspect = (img && img.naturalWidth > 0) ? img.naturalWidth / img.naturalHeight : 0.9;
    const feetY = baseH * 0.5 - p.radius * 0.5;
    let rx = Math.max(p.radius * 3.0, baseH * aspect * 0.30);
    let ry = p.radius * 1.1;
    if (p.boss === "titan") { rx *= 1.12; ry *= 1.08; }
    if (p.boss === "groshkar") { rx *= 1.12; ry *= 1.08; }
    const sdx = rx * 0.16;
    // shadow cast toward lower-right (scene light comes from upper-left)
    c.fillStyle = "rgba(0,0,0,0.14)";
    c.beginPath(); c.ellipse(sdx, feetY, rx * 1.14, ry * 1.18, 0, 0, TAU); c.fill();
    c.fillStyle = "rgba(0,0,0,0.40)";
    c.beginPath(); c.ellipse(sdx, feetY, rx, ry, 0, 0, TAU); c.fill();
    c.fillStyle = "rgba(0,0,0,0.55)";
    c.beginPath(); c.ellipse(sdx, feetY, rx * 0.40, ry * 0.40, 0, 0, TAU); c.fill();
    return;
  }
  c.fillStyle = "rgba(0,0,0,0.35)";
  c.beginPath(); c.ellipse(p.radius * 0.35, p.radius * 0.7, p.radius * 0.9, p.radius * 0.4, 0, 0, TAU); c.fill();
}

export function makeWizardSprite(robe, trim) {
  const cv = document.createElement("canvas");
  cv.width = cv.height = SPRITE_SIZE;
  const c = cv.getContext("2d");
  c.translate(SPRITE_SIZE / 2, SPRITE_SIZE / 2);
  c.lineJoin = "round"; c.lineCap = "round";

  const dark = shade(robe, 0.42);
  const mid = shade(robe, 0.7);
  const light = shade(robe, 1.28);
  const outline = "rgba(10,8,20,0.92)";
  let g;

  // ===== CLOAK (trailing behind, to the back/left, tattered hem) =====
  c.fillStyle = dark; c.strokeStyle = outline; c.lineWidth = 3;
  c.beginPath();
  c.moveTo(-2, -10);
  c.quadraticCurveTo(-22, -2, -26, 18);
  c.lineTo(-34, 42); c.lineTo(-25, 50); c.lineTo(-19, 40);
  c.lineTo(-11, 52); c.lineTo(-5, 42);
  c.lineTo(-2, 30);
  c.quadraticCurveTo(-6, 6, -2, -10);
  c.closePath(); c.fill(); c.stroke();

  // ===== BACK ARM/SLEEVE (behind body) =====
  c.strokeStyle = dark; c.lineWidth = 9;
  c.beginPath(); c.moveTo(-6, 4); c.quadraticCurveTo(-12, 14, -8, 22); c.stroke();

  // ===== ROBE BODY (bell, leaning slightly forward) =====
  g = c.createLinearGradient(0, -14, 0, 46);
  g.addColorStop(0, light); g.addColorStop(0.55, robe); g.addColorStop(1, dark);
  c.fillStyle = g; c.strokeStyle = outline; c.lineWidth = 3;
  c.beginPath();
  c.moveTo(-15, -6);
  c.quadraticCurveTo(-22, 28, -16, 46);
  c.quadraticCurveTo(2, 52, 19, 45);
  c.quadraticCurveTo(23, 26, 15, -6);
  c.quadraticCurveTo(0, -15, -15, -6);
  c.closePath(); c.fill(); c.stroke();
  // front seam + belt cinch
  c.strokeStyle = rgba(trim, 0.5); c.lineWidth = 2.5;
  c.beginPath(); c.moveTo(4, -2); c.quadraticCurveTo(7, 22, 3, 45); c.stroke();
  c.fillStyle = trim;
  c.beginPath(); c.ellipse(3, 18, 5.5, 4, 0.1, 0, TAU); c.fill();

  // ===== FRONT ARM/SLEEVE reaching forward, cupping the orb =====
  c.strokeStyle = mid; c.lineWidth = 10;
  c.beginPath(); c.moveTo(8, 2); c.quadraticCurveTo(20, 0, 22, 11); c.stroke();
  c.strokeStyle = outline; c.lineWidth = 1.5;
  c.beginPath(); c.moveTo(8, 2); c.quadraticCurveTo(20, 0, 22, 11); c.stroke();

  // ===== SHOULDER PAULDRONS (front prominent, back smaller) =====
  c.fillStyle = mid; c.strokeStyle = outline; c.lineWidth = 2;
  c.beginPath(); c.ellipse(-10, -5, 5, 4, -0.4, 0, TAU); c.fill(); c.stroke();
  c.beginPath(); c.ellipse(9, -5, 7.5, 5.5, 0.35, 0, TAU); c.fill(); c.stroke();

  // ===== HOOD (pointed cowl, peak + opening leaning forward to +X) =====
  g = c.createLinearGradient(-8, -48, 12, -6);
  g.addColorStop(0, mid); g.addColorStop(0.5, robe); g.addColorStop(1, light);
  c.fillStyle = g; c.strokeStyle = outline; c.lineWidth = 3;
  c.beginPath();
  c.moveTo(-12, -6);                      // back base (neck)
  c.quadraticCurveTo(-17, -34, -2, -49);  // up the back to the crown
  c.quadraticCurveTo(9, -57, 19, -41);    // peak bent forward-right
  c.quadraticCurveTo(21, -28, 16, -15);   // down the front
  c.quadraticCurveTo(13, -7, 3, -6);      // front opening lip
  c.quadraticCurveTo(-5, -4, -12, -6);    // across the underside to the back
  c.closePath(); c.fill(); c.stroke();
  // crown fold highlight
  c.strokeStyle = rgba(light, 0.5); c.lineWidth = 2;
  c.beginPath(); c.moveTo(-4, -10); c.quadraticCurveTo(2, -36, 12, -44); c.stroke();
  // front-opening rim trim
  c.strokeStyle = trim; c.lineWidth = 2.5;
  c.beginPath(); c.moveTo(3, -6); c.quadraticCurveTo(15, -10, 15, -22); c.stroke();

  // ===== FACE CAVITY (deep shadow inside the cowl) + glowing eyes toward front =====
  c.fillStyle = "rgba(0,0,0,0.85)";
  c.beginPath(); c.ellipse(6, -21, 9, 11, 0.12, 0, TAU); c.fill();
  c.save();
  c.shadowColor = trim; c.shadowBlur = 9;
  c.fillStyle = trim;
  c.beginPath(); c.ellipse(8, -23, 2.2, 3.1, -0.2, 0, TAU); c.fill();
  c.beginPath(); c.ellipse(12.5, -22, 2.0, 2.8, -0.2, 0, TAU); c.fill();
  c.fillStyle = "#ffffff";
  c.beginPath(); c.arc(8, -24, 0.9, 0, TAU); c.fill();
  c.beginPath(); c.arc(12.5, -23, 0.8, 0, TAU); c.fill();
  c.restore();

  // ===== CHANNELED ORB (cupped in the outstretched front hand) =====
  c.save();
  c.shadowColor = trim; c.shadowBlur = 14;
  g = c.createRadialGradient(24, 11, 1, 24, 11, 8);
  g.addColorStop(0, "#ffffff"); g.addColorStop(0.4, trim); g.addColorStop(1, rgba(trim, 0));
  c.fillStyle = g;
  c.beginPath(); c.arc(24, 11, 8, 0, TAU); c.fill();
  c.restore();

  return cv;
}

// Root-relative asset paths (SPA routes like /lobby/CODE must not break fetch URLs).
export function assetURL(path) {
  return path.charAt(0) === "/" ? path : "/" + path;
}
